import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Card, Field, Flex, Input, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { roleCreationSchema } from "../schema/roles.schema";
import { AppRoutes } from "@/constants/routes";
import { PermissionsTable } from "@/components/common/section-components/PermissionsTable";
import { getListadoPermisos } from "@/services/users-bo.service";
import { useEffect, useState } from "react";
import { Permiso } from "@/pages/role-asignation/schema/role-assignation.schema";
import { useRoles } from "@/hooks/useRoles";
import { handleCreateRole } from "../schema/handler-functions";
import { toaster } from "@/components/ui/toaster";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";

export const CreateRolePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const navigate = useNavigate();

  const [loadingCreateRol, setLoadingCreateRol] = useState<boolean>(false);

  const [loadingPermissions, setLoadingPermissions] = useState<boolean>(false);

  const handleCancel = () => {
    navigate(-1);
  };

  const [permissions, setPermissions] = useState<Permiso[] | null>([]);

  const fetchPermissions = async () => {
    try {
      
      setLoadingPermissions(true);
      const response = await getListadoPermisos();
  
      setPermissions(response);
  
      setLoadingPermissions(false);
    } catch (error) {
     const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.ROLES,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.message ?? "No se ha podido obtener los permisos.",
        });
        throw error;
      }
    }
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchPermissions();
  }, [isActive]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roleCreationSchema),
    defaultValues: {
      name: "",
      permissions: [],
    },
    mode: "onChange",
  });

  const setPermissionSelection = async (value?: number[]) => {
    setValue("permissions", value ?? []);
  };

  const { fetchPermissions: fetchUserPermissions } = useRoles();
  const onSubmit = async (event: FieldValues) => {
    handleCreateRole({
      data: event,
      fetchPermissions: fetchUserPermissions,
      navigate,
      redirectPath: AppRoutes.ROLES,
      setLoading: setLoadingCreateRol,
    });
  };

  const [permissionsSelection, name] = useWatch({
    name: ["permissions", "name"],
    control,
  });

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="create-role-page">
      <SectionHeader
        title={`Alta de Rol`}
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        disableAdd={!permissionsSelection?.length || !name?.length}
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        loading={loadingCreateRol || loadingPermissions}
      />
      {!loadingPermissions && (
        <Card.Root maxW="full" marginTop="30px">
          <Card.Body paddingX="48px" paddingY="30px">
            <Flex flexDir="column" gap="30px">
              <Field.Root
                invalid={!!errors?.name?.message}
                display="flex"
                flexDir="column"
                gap="12px"
                maxWidth="300px"
              >
                <Field.Label>
                  <Text fontWeight={600} fontSize="14px">
                    Nombre
                  </Text>
                </Field.Label>
                <Input
                  placeholder="Nombre del rol"
                  paddingLeft="12px"
                  {...register("name")}
                  disabled={loadingCreateRol || loadingPermissions}
                />
                <Field.ErrorText>{errors?.name?.message}</Field.ErrorText>
              </Field.Root>
              <PermissionsTable
                permissions={permissions || []}
                permissionSelection={permissionsSelection}
                setPermissionSelection={setPermissionSelection}
                disableAll={loadingCreateRol || loadingPermissions}
              />
            </Flex>
          </Card.Body>
        </Card.Root>
      )}
    </Flex>
  );
};
